/**
 * eDiscovery (PRD Phase 6.5.2, ADR-0021): search the organisation's
 * compliance archive, review a message read-only, and export a result set
 * with a chain-of-custody manifest.
 *
 * eDiscovery is its own role, not administration, so its routes are not
 * under the admin base: `AdminWhereContext.ediscovery` names them, for the
 * platform console and for webmail alike. Every search, view, export and
 * download is audit-logged by the API.
 */

import { Fragment, useContext, useEffect, useState } from 'react';
import type {
  EDiscoveryExport,
  EDiscoveryExportCreated,
  EDiscoveryExportList,
  EDiscoveryFormat,
  EDiscoveryHit,
  EDiscoveryMessage,
  EDiscoveryResults,
  EDiscoverySearch,
  NewEDiscoveryExport,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { AdminWhereContext, size } from './mailboxes';
import { Badge, CopyButton, Loaded, useLoad, when } from './ui';

// ─── The search ──────────────────────────────────────────────────────────────

type TextField = 'participant' | 'from' | 'subject' | 'text' | 'since' | 'before';

const FIELDS: ReadonlyArray<[TextField, string, 'text' | 'date', string]> = [
  ['participant', 'Participant', 'text', 'jane@acme.example or @acme.example'],
  ['from', 'From', 'text', 'jane@acme.example'],
  ['subject', 'Subject contains', 'text', ''],
  ['text', 'Words', 'text', '"exact phrase" invoice or receipt -draft'],
  ['since', 'Sent since', 'date', ''],
  ['before', 'Sent before', 'date', ''],
];

type Form = Record<TextField, string> & { has_attachments: boolean };

const EMPTY: Form = {
  participant: '',
  from: '',
  subject: '',
  text: '',
  since: '',
  before: '',
  has_attachments: false,
};

/** The search the form describes: only the fields filled in. */
export function searchOf(form: Form): EDiscoverySearch {
  const search: Record<string, string | boolean> = {};
  for (const [key] of FIELDS) {
    const value = form[key].trim();
    if (value) {
      search[key] = value;
    }
  }
  if (form.has_attachments) {
    search.has_attachments = true;
  }
  return search as EDiscoverySearch;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** The other participants, the first two named and the rest counted. */
export function othersInWords(hit: EDiscoveryHit): { short: string; all: string } {
  const from = hit.from_addr?.toLowerCase();
  const others = hit.participants.filter((p) => p.toLowerCase() !== from);
  const shown = others.slice(0, 2).join(', ');
  const more = others.length - 2;
  return {
    short: others.length === 0 ? '—' : more > 0 ? `${shown} +${more} more` : shown,
    all: others.join(', '),
  };
}

// ─── The panel ───────────────────────────────────────────────────────────────

interface Ran {
  /** The search as sent, which an export repeats exactly. */
  readonly search: EDiscoverySearch;
  readonly results: EDiscoveryResults;
}

export function EDiscoveryPanel({ slug }: { slug: string }) {
  const { ediscovery } = useContext(AdminWhereContext);
  const root = ediscovery(slug);
  const [form, setForm] = useState<Form>(EMPTY);
  const [ran, setRan] = useState<Ran | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [exports, reloadExports] = useLoad(
    (signal) => api<EDiscoveryExportList>(`${root}/exports`, { signal }),
    `ediscovery-exports:${root}`,
  );

  return (
    <>
      <section className="adm-panel ed">
        <h3>eDiscovery</h3>
        <p className="adm-muted">
          Search every message the compliance archive has journaled for this organisation, review
          one without altering it, and export what a matter needs. eDiscovery is granted separately
          from administration (the tenant group <code>ZitiMail-eDiscovery</code>), and every search,
          view and download is recorded in the audit log.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const search = searchOf(form);
            setBusy(true);
            setError(null);
            setOpen(null);
            api<EDiscoveryResults>(`${root}/search`, { method: 'POST', body: { search } })
              .then((results) => setRan({ search, results }))
              .catch((e: unknown) => {
                setRan(null);
                setError(describeError(e));
              })
              .finally(() => setBusy(false));
          }}
        >
          <div className="adm-form-grid">
            {FIELDS.map(([key, label, type, placeholder]) => (
              <label key={key}>
                {label}
                <input
                  type={type}
                  value={form[key]}
                  placeholder={placeholder || undefined}
                  spellCheck={false}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </label>
            ))}
            <label className="adm-check">
              <input
                type="checkbox"
                checked={form.has_attachments}
                onChange={(e) => setForm({ ...form, has_attachments: e.target.checked })}
              />
              Has attachments
            </label>
          </div>
          <p className="adm-muted adm-small">
            Every field you fill in must match. Participant is an address in From, To, Cc or Bcc, or{' '}
            <span className="adm-mono">@domain</span> for anyone there. Words searches the subject,
            addresses, body and attachment names: put a phrase in quotes, use{' '}
            <span className="adm-mono">or</span> between alternatives and{' '}
            <span className="adm-mono">-word</span> to leave a word out.
          </p>
          <div className="adm-inline">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Searching…' : 'Search'}
            </button>
            {(ran || error) && (
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setForm(EMPTY);
                  setRan(null);
                  setError(null);
                  setOpen(null);
                }}
              >
                Clear
              </button>
            )}
            {error && (
              <span className="adm-error" role="alert">
                {error}
              </span>
            )}
          </div>
        </form>

        {ran && <Results results={ran.results} onOpen={setOpen} open={open} />}
        {ran && ran.results.total > 0 && (
          <ExportForm
            key={JSON.stringify(ran.search)}
            root={root}
            search={ran.search}
            total={ran.results.total}
            onCreated={reloadExports}
          />
        )}
      </section>

      <section className="adm-panel ed">
        <h3>Exports</h3>
        <p className="adm-muted">
          Chain of custody: an export&apos;s manifest lists every message with the SHA-256 taken
          when it was journaled, and the manifest&apos;s own SHA-256 is recorded in the audit log
          when the export is created. So the export can be checked later, the manifest against the
          audit log and each message against the manifest.
        </p>
        <Loaded load={exports}>
          {(list) => <ExportsList root={root} exports={list.exports} />}
        </Loaded>
      </section>

      {open && <Review key={open} root={root} id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

// ─── Results ─────────────────────────────────────────────────────────────────

function Results({
  results,
  open,
  onOpen,
}: {
  results: EDiscoveryResults;
  open: string | null;
  onOpen: (id: string) => void;
}) {
  const { total, total_bytes, hits } = results;
  return (
    <div className="adm-sub">
      <p role="status" className="ed-count">
        <strong>
          {plural(total, 'message', 'messages')} ({size(total_bytes)}) match
        </strong>
        {total > hits.length && (
          <span className="adm-muted">, showing the newest {hits.length.toLocaleString()}</span>
        )}
      </p>
      {hits.length > 0 && (
        <div className="adm-scroll">
          <table className="adm-table ed-hits">
            <thead>
              <tr>
                <th>Sent</th>
                <th>From</th>
                <th>Other participants</th>
                <th>Subject</th>
                <th>Attachments</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              {hits.map((hit) => {
                const others = othersInWords(hit);
                return (
                  <tr
                    key={hit.id}
                    className={open === hit.id ? 'on' : undefined}
                    onClick={() => onOpen(hit.id)}
                  >
                    <td className="ed-nowrap">{when(hit.sent_at ?? hit.journaled_at)}</td>
                    <td>{hit.from_addr ?? '—'}</td>
                    <td title={others.all || undefined}>{others.short}</td>
                    <td>
                      <button
                        type="button"
                        className="ed-open"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(hit.id);
                        }}
                      >
                        {hit.subject || '(no subject)'}
                      </button>
                    </td>
                    <td className="ed-nowrap">
                      {hit.attachments > 0
                        ? plural(hit.attachments, 'attachment', 'attachments')
                        : ''}
                    </td>
                    <td className="ed-nowrap">{size(hit.size_bytes)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Review ──────────────────────────────────────────────────────────────────

function Review({ root, id, onClose }: { root: string; id: string; onClose: () => void }) {
  const [load] = useLoad(
    (signal) => api<EDiscoveryMessage>(`${root}/messages/${encodeURIComponent(id)}`, { signal }),
    `ediscovery-message:${root}:${id}`,
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ed-review" role="dialog" aria-label="Review message">
      <div className="adm-panel-head">
        <h3>Review</h3>
        <button type="button" className="btn ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="adm-muted adm-small">
        Read-only. Opening a message is recorded in the audit log.
      </p>
      <Loaded load={load}>{(m) => <ReviewBody found={m} />}</Loaded>
    </div>
  );
}

function ReviewBody({ found }: { found: EDiscoveryMessage }) {
  const { message, intact, headers, text, attachments } = found;
  const rows: Array<[string, string | null]> = headers
    ? [
        ['From', headers.from.join(', ')],
        ['To', headers.to.join(', ')],
        ['Cc', headers.cc.join(', ')],
        ['Bcc', headers.bcc.join(', ')],
        ['Subject', headers.subject],
        ['Date', headers.date ? when(headers.date) : null],
        ['Message-ID', headers.message_id],
      ]
    : [];
  return (
    <>
      {intact ? (
        <p className="adm-small">
          <Badge tone="ok">intact</Badge> The archived copy matches the hash taken when it was
          journaled.
        </p>
      ) : (
        <div className="ed-alert" role="alert">
          <strong>This message is not intact</strong>
          The archived copy no longer matches the SHA-256 taken when it was journaled, so what is
          shown here may not be what was sent or received. An export that includes it will stop at
          this message. Report it to Genworx.
        </div>
      )}
      {headers ? (
        <dl className="adm-facts ed-headers">
          {rows
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </Fragment>
            ))}
        </dl>
      ) : (
        <p className="adm-muted">The archived copy could not be read as a message.</p>
      )}
      <h4>Body text</h4>
      {text ? (
        <pre className="ed-body">{text}</pre>
      ) : (
        <p className="adm-muted adm-small">No text body.</p>
      )}
      <h4>Attachments</h4>
      {attachments.length === 0 ? (
        <p className="adm-muted adm-small">None.</p>
      ) : (
        <ul className="ed-attachments adm-small">
          {attachments.map((a, i) => (
            <li key={i}>
              {a.name ?? '(unnamed)'}{' '}
              <span className="adm-muted">
                {a.type ?? 'unknown type'} · {size(a.size)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <dl className="adm-facts adm-small ed-custody">
        <dt>Journaled</dt>
        <dd>{when(message.journaled_at)}</dd>
        <dt>SHA-256</dt>
        <dd className="adm-mono ed-hash">{message.sha256}</dd>
        <dt>Archive key</dt>
        <dd className="adm-mono ed-hash">{message.object_key}</dd>
      </dl>
    </>
  );
}

// ─── Exporting ───────────────────────────────────────────────────────────────

function ExportForm({
  root,
  search,
  total,
  onCreated,
}: {
  root: string;
  search: EDiscoverySearch;
  total: number;
  onCreated: () => void;
}) {
  const [matter, setMatter] = useState('');
  const [format, setFormat] = useState<EDiscoveryFormat>('eml');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<EDiscoveryExportCreated | null>(null);
  return (
    <form
      className="adm-sub ed-export"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        setCreated(null);
        const body: NewEDiscoveryExport = { matter: matter.trim(), format, search };
        api<EDiscoveryExportCreated>(`${root}/exports`, { method: 'POST', body })
          .then((done) => {
            setCreated(done);
            setMatter('');
            onCreated();
          })
          .catch((e: unknown) => setError(describeError(e)))
          .finally(() => setBusy(false));
      }}
    >
      <h4>Export these {plural(total, 'message', 'messages')}</h4>
      <div className="adm-form-grid">
        <label>
          Matter
          <input
            value={matter}
            onChange={(e) => setMatter(e.target.value)}
            placeholder="e.g. Smith v Acme"
            required
          />
        </label>
      </div>
      <div className="adm-inline ed-format" role="radiogroup" aria-label="Format">
        <label className="adm-check">
          <input
            type="radio"
            name="ed-format"
            checked={format === 'eml'}
            onChange={() => setFormat('eml')}
          />
          EML files with a manifest (one .tar)
        </label>
        <label className="adm-check">
          <input
            type="radio"
            name="ed-format"
            checked={format === 'mbox'}
            onChange={() => setFormat('mbox')}
          />
          One MBOX file
        </label>
      </div>
      <p className="adm-muted adm-small">
        The export holds the messages exactly as journaled. It can be downloaded for 30 days.
      </p>
      <div className="adm-inline">
        <button type="submit" className="btn" disabled={busy || matter.trim() === ''}>
          {busy ? 'Creating…' : 'Create export'}
        </button>
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
        {created && (
          <span className="adm-ok" role="status">
            Export created: {plural(created.message_count, 'message', 'messages')} (
            {size(created.total_bytes)}).
          </span>
        )}
      </div>
    </form>
  );
}

const FORMAT_LABEL: Record<EDiscoveryFormat, string> = {
  eml: 'EML with manifest',
  mbox: 'MBOX',
};

function ExportsList({ root, exports }: { root: string; exports: readonly EDiscoveryExport[] }) {
  if (exports.length === 0) {
    return <p className="adm-muted">No exports yet.</p>;
  }
  const now = Date.now();
  return (
    <div className="adm-scroll">
      <table className="adm-table ed-exports">
        <thead>
          <tr>
            <th>Matter</th>
            <th>Messages</th>
            <th>Format</th>
            <th>Manifest SHA-256</th>
            <th>Downloads</th>
            <th>Expires</th>
            <th>Files</th>
          </tr>
        </thead>
        <tbody>
          {exports.map((x) => {
            const files = `/api/v1${root}/exports/${encodeURIComponent(x.id)}`;
            const expired = new Date(x.expires_at).getTime() <= now;
            return (
              <tr key={x.id}>
                <td>
                  <strong>{x.matter}</strong>
                  <div className="adm-muted adm-small">
                    {x.created_by}, {when(x.created_at)}
                  </div>
                </td>
                <td className="ed-nowrap">
                  {plural(x.message_count, 'message', 'messages')}
                  <div className="adm-muted adm-small">{size(x.total_bytes)}</div>
                </td>
                <td className="ed-nowrap">{FORMAT_LABEL[x.format] ?? x.format}</td>
                <td>
                  <span className="adm-mono ed-hash">{x.manifest_sha256}</span>
                  <CopyButton text={x.manifest_sha256} />
                </td>
                <td>{x.downloads.toLocaleString()}</td>
                <td className="ed-nowrap">
                  {expired ? <Badge tone="neutral">expired</Badge> : when(x.expires_at)}
                </td>
                <td className="ed-nowrap">
                  {!expired && (
                    <span className="adm-actions">
                      <a className="btn ghost" href={`${files}/download`} download>
                        Download
                      </a>
                      <a className="btn ghost" href={`${files}/manifest`} download>
                        Manifest
                      </a>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
